import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Flame, ArrowRight, Trophy } from "lucide-react";
import { Layout } from "../components/Layout";
import { getIcon } from "../lib/icons";
import { PageRocket } from "../components/PageRocket";
import { fetchTrendingCategories } from "../lib/api";

export default function Trending() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTrendingCategories()
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Layout>
      <section className="max-w-5xl mx-auto px-6 lg:px-8 py-14" data-testid="trending-page">
        <div className="flex items-start justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-[#D4AF37]/12 px-4 py-1.5 text-xs font-semibold text-[#B8901F] mb-4">
              <Flame size={14} /> Trending Opportunities
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-[#0F0F0F]">
              What founders are loving right now
            </h1>
            <p className="text-[#687280] mt-3 max-w-xl">
              The most-upvoted categories, ranked by the community. Tap the thumbs-up on any category to push it up the list.
            </p>
          </div>
          <PageRocket className="w-36 lg:w-52 -mt-2" />
        </div>

        {loading ? (
          <p className="text-[#687280]" data-testid="trending-loading">Loading trending opportunities…</p>
        ) : items.length === 0 ? (
          <div className="bg-[#F7F7F7] rounded-2xl border border-dashed border-gray-200 py-16 px-8 text-center" data-testid="trending-empty-state">
            <div className="w-12 h-12 rounded-xl bg-white text-[#D4AF37] flex items-center justify-center mx-auto mb-4">
              <Flame size={22} />
            </div>
            <p className="font-display text-xl font-semibold text-[#0F0F0F]">No trending opportunities yet</p>
            <p className="text-[#687280] mt-2 max-w-md mx-auto">
              Be the first to upvote — head to Categories and tap the thumbs-up on the ones you love.
            </p>
            <button
              onClick={() => navigate("/categories")}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#0F0F0F] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors"
              data-testid="trending-browse-categories-btn"
            >
              Explore Categories <ArrowRight size={16} />
            </button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="trending-list">
            {items.map((c, i) => {
              const Icon = getIcon(c.icon);
              const rank = i + 1;
              const topThree = rank <= 3;
              return (
                <div
                  key={c.slug}
                  onClick={() => navigate(`/categories/${c.slug}`)}
                  role="button"
                  className="group flex items-center gap-5 bg-white rounded-2xl p-5 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_40px_rgb(0,0,0,0.08)] hover:-translate-y-0.5 transition-all duration-300 cursor-pointer"
                  data-testid={`trending-card-${c.slug}`}
                >
                  <div
                    className={`shrink-0 w-10 h-10 rounded-full flex items-center justify-center font-display font-bold text-sm ${
                      topThree ? "bg-[#D4AF37] text-[#0F0F0F]" : "bg-[#F7F7F7] text-[#687280]"
                    }`}
                  >
                    {topThree ? <Trophy size={16} /> : rank}
                  </div>
                  <div
                    className="shrink-0 w-14 h-14 rounded-2xl flex items-center justify-center"
                    style={{ backgroundColor: `${c.color}18`, color: c.color }}
                  >
                    <Icon size={26} strokeWidth={1.75} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-display font-semibold text-lg text-[#0F0F0F] truncate">{c.name}</h3>
                    <p className="text-sm text-[#687280] line-clamp-1">{c.description}</p>
                  </div>
                  <ArrowRight size={18} className="shrink-0 text-[#687280] transition-transform duration-300 group-hover:translate-x-1 group-hover:text-[#D4AF37]" />
                </div>
              );
            })}
          </div>
        )}
      </section>
    </Layout>
  );
}
